// What the Rivals tab, its dot and Today's strip make of your challenges: one list of players per
// edition (scores come from plays, so someone counts once however many challenges you share), whose
// turn it is, and how each day came out.
import type { Challenge, ChallengePlayer, EditionChallenges, MatchResult } from '@wbwl/contract'

export interface Day {
  readonly edition: number
  readonly date: string
  readonly challenges: readonly Challenge[]
  /** Everyone you're up against that day, you included: finished (best first), new results, playing, invited. */
  readonly players: readonly ChallengePlayer[]
  readonly me: ChallengePlayer | null
  /** In-app invites you haven't answered. */
  readonly invites: readonly Challenge[]
  /** Your own challenge on the edition, if you made one. */
  readonly own: Challenge | null
  /** The challenge to open for the day's boards and chat: yours, else one you've joined. */
  readonly open: Challenge | null
  /** Invites or a challenge you've joined, on an edition you haven't finished: play it. */
  readonly yourTurn: boolean
  /** Invites to an edition you've already finished: accept to compare. */
  readonly toAccept: readonly Challenge[]
  /** Players you're only invited to face: no result against them counts until you accept. */
  readonly invitedOnly: ReadonlySet<string>
  /** Results that came in since you last looked, still hidden. */
  readonly fresh: readonly ChallengePlayer[]
  /** The others whose scores you're chasing: finished, best first. */
  readonly toBeat: readonly ChallengePlayer[]
}

const tier = (p: ChallengePlayer) => (p.status === 'invited' ? 4 : p.done ? (isFresh(p) ? 1 : 0) : p.rows > 0 ? 2 : 3)
const isFresh = (p: ChallengePlayer) => p.fresh && p.done && !p.mine

export function dayOf(group: EditionChallenges): Day {
  // Prefer what a challenge you've joined says (it lists the whole day and has your records), then a joined status.
  const byId = new Map<string, { p: ChallengePlayer; joined: boolean }>()
  for (const ch of group.challenges) {
    const joined = ch.me === 'joined'
    for (const p of ch.players) {
      const had = byId.get(p.id)
      if (!had || (joined && !had.joined) || (joined === had.joined && had.p.status === 'invited' && p.status === 'joined')) byId.set(p.id, { p, joined })
    }
  }
  const players = [...byId.values()].map((x) => x.p).sort((a, b) =>
    tier(a) - tier(b) || (b.total ?? 0) - (a.total ?? 0) || b.rows - a.rows || a.displayName.localeCompare(b.displayName))
  const me = players.find((p) => p.mine) ?? null
  const joined = group.challenges.filter((c) => c.me === 'joined')
  const invites = group.challenges.filter((c) => c.me === 'invited')
  const own = group.challenges.find((c) => c.creator.id === me?.id) ?? null
  return {
    edition: group.edition,
    date: group.date,
    challenges: group.challenges,
    players,
    me,
    invites,
    own,
    open: own ?? joined[0] ?? null,
    yourTurn: !me?.done && (invites.length > 0 || joined.length > 0),
    toAccept: me?.done ? invites : [],
    invitedOnly: new Set([...byId].filter(([, x]) => !x.joined).map(([id]) => id)),
    fresh: players.filter(isFresh),
    toBeat: players.filter((p) => !p.mine && p.status === 'joined' && p.done && !isFresh(p)),
  }
}

/** Something on the day waits for you: your turn, an invite to accept, or a result you haven't seen. */
export const needsYou = (d: Day) => d.yourTurn || d.toAccept.length > 0 || (!!d.me?.done && d.fresh.length > 0)

/** Your result against one player that day, once you've both finished, you've seen theirs, and it counts. */
export function resultAgainst(d: Day, p: ChallengePlayer): MatchResult | null {
  const me = d.me
  if (!me?.done || !p.done || p.mine || isFresh(p) || d.invitedOnly.has(p.id) || me.total === null || p.total === null) return null
  return me.total > p.total ? 'w' : me.total < p.total ? 'l' : 'd'
}

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th … 21st, 22nd. */
export function ordinal(n: number): string {
  const teen = n % 100 >= 11 && n % 100 <= 13
  return `${n}${teen ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
}

/**
 * How the day came out for you, once you've finished and seen at least one other result: won, lost
 * or tied against one player, else your place ("2nd of 4"), which counts as a win when it's first.
 */
export function outcome(d: Day): { label: string; result: MatchResult | null } | null {
  const others = d.players.filter((p) => !p.mine && p.status === 'joined' && p.done && !isFresh(p) && !d.invitedOnly.has(p.id))
  if (!d.me?.done || others.length === 0) return null
  const mine = d.me.total ?? 0
  const ahead = others.filter((p) => (p.total ?? 0) > mine).length
  const level = others.some((p) => p.total === mine)
  if (others.length === 1) return ahead ? { label: 'Lost', result: 'l' } : level ? { label: 'Tied', result: 'd' } : { label: 'Won', result: 'w' }
  if (ahead === 0) return level ? { label: 'Tied 1st', result: 'd' } : { label: 'Won', result: 'w' }
  return { label: `${ordinal(ahead + 1)} of ${others.length + 1}`, result: null }
}

/** "Talon", "Talon and Dad", "Talon and 2 others". */
export function namesOf(players: readonly ChallengePlayer[]): string {
  const names = players.map((p) => p.displayName)
  if (names.length < 3) return names.join(' and ')
  return `${names[0]} and ${names.length - 1} others`
}
